/**
 * Save a rendered element as an A4 PDF.
 *
 * Viewers keep their content in scrolling panels, and html2canvas then
 * photographs the visible window. The element is cloned into the document
 * with scrolling and height limits removed so the file is the full report.
 * A fixed box parked off the left edge is still captured as the screen, so
 * the clone sits at the document origin instead.
 */
export async function downloadElementPdf(element, filename, options = {}) {
  if (!element) throw new Error('Nothing to export');
  const { default: html2pdf } = await import('html2pdf.js');

  const holder = document.createElement('div');
  holder.style.cssText =
    'position:absolute;left:0;top:0;z-index:-1;background:#fff;pointer-events:none;';
  const width = Math.max(element.scrollWidth, element.offsetWidth, 760);
  holder.style.width = `${width}px`;

  const clone = element.cloneNode(true);
  const unclip = (node) => {
    node.style.maxHeight = 'none';
    node.style.height = 'auto';
    node.style.overflow = 'visible';
  };
  unclip(clone);
  // html2pdf lays the clone out in a page-wide (~730px) box; wider layouts
  // would spill past it and be cut off. Keep the real width; the capture is
  // then scaled down to fit A4.
  clone.style.width = `${width}px`;
  clone.style.maxWidth = 'none';
  // Buttons and other UI chrome marked to skip stay out of the document.
  clone.querySelectorAll('[data-pdf-ignore]').forEach((node) => node.remove());
  holder.appendChild(clone);
  document.body.appendChild(holder);
  // Computed styles exist only once attached.
  clone.querySelectorAll('*').forEach((node) => {
    const style = window.getComputedStyle(node);
    if (style.overflowY === 'auto' || style.overflowY === 'scroll') unclip(node);
  });

  try {
    const worker = html2pdf()
      .set({
        margin: 8,
        filename: String(filename || 'document.pdf').replace(/[\\/:*?"<>|]+/g, '-'),
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          scrollX: 0,
          scrollY: 0,
          backgroundColor: '#ffffff',
          width,
          windowWidth: width,
        },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'] },
        enableLinks: true,
      })
      .from(clone)
      .toPdf();
    const pdf = await worker.get('pdf');
    appendPhotoLinks(pdf, options.links);
    const safeName = String(filename || 'document.pdf').replace(/[\\/:*?"<>|]+/g, '-');
    if (options.output === 'blob') return pdf.output('blob');
    pdf.save(safeName);
    return null;
  } finally {
    holder.remove();
  }
}

/** Clickable photo URLs on their own page. The report pages are a picture, so links cannot sit on the images themselves. */
function appendPhotoLinks(pdf, links) {
  const rows = (Array.isArray(links) ? links : []).filter((row) =>
    /^https?:\/\//i.test(String(row?.url || '')),
  );
  if (!rows.length || !pdf?.addPage) return;
  pdf.addPage();
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(14);
  pdf.setTextColor(30, 27, 75);
  pdf.text('Photo links', 14, 18);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(71, 85, 105);
  pdf.text('Click a link to open the photo.', 14, 26);
  let y = 38;
  rows.forEach((row, index) => {
    if (y > 270) {
      pdf.addPage();
      y = 20;
    }
    pdf.setTextColor(15, 23, 42);
    pdf.text(`${index + 1}. ${String(row.label || 'Photo').slice(0, 90)}`, 14, y);
    pdf.setTextColor(79, 70, 229);
    pdf.textWithLink('Open photo', 14, y + 6, { url: String(row.url) });
    y += 16;
  });
}

/**
 * Download a stored file (S3 link). Cross-origin links ignore the `download`
 * attribute and open in the tab instead, so fetch the bytes and save them;
 * if the storage blocks that, fall back to opening it in a new tab.
 */
export async function downloadStoredFile(url, filename) {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = filename || 'file';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 1500);
    return true;
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
    return false;
  }
}
